import EditablePayrollRow from "./EditablePayrollRow";
import "./PayrollRulesPage.css";
// src/popup/ObRulesPopup.js
import React, { useEffect, useState } from "react";
import {
  FaSave,
  FaClock,
  FaCloudMoon,
  FaArrowLeft,
  FaCalendarAlt,
  FaList,
  FaDownload,
} from "react-icons/fa";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  getDocs,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useCompany } from "../contexts/CompanyContext";

// Popup-komponent för att hantera OB-regler och helgdagar
export default function ObRulesPopup({ isOpen, onClose }) {
  const { activeCompany } = useCompany();
  const currentYear = new Date().getFullYear();

  // State för UI
  const [activeTab, setActiveTab] = useState("obRules");
  const [showCreatePanel, setShowCreatePanel] = useState(false);

  // State för OB-regler
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [selectedDays, setSelectedDays] = useState([]);
  const [savedRules, setSavedRules] = useState([]);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // State för helgdagar
  const [savedHolidays, setSavedHolidays] = useState([]);
  const [editingHolidayId, setEditingHolidayId] = useState(null);
  const [fetchingHolidays, setFetchingHolidays] = useState(false);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [lastFetchedYear, setLastFetchedYear] = useState(null);
  const [fetchedYears, setFetchedYears] = useState(new Set());
  const [holidayName, setHolidayName] = useState("");
  const [holidayDate, setHolidayDate] = useState("");
  const [holidayYear, setHolidayYear] = useState(currentYear);
  const [holidayAmount, setHolidayAmount] = useState("");
  const [holidayStartTime, setHolidayStartTime] = useState("");
  const [holidayEndTime, setHolidayEndTime] = useState("");

  // Hämta företagskontots UID
  const accountToUse = activeCompany?.companyAccountUid;

  // Veckodagar på svenska
  const veckodagsalternativ = [
    { value: "måndag", label: "Måndag" },
    { value: "tisdag", label: "Tisdag" },
    { value: "onsdag", label: "Onsdag" },
    { value: "torsdag", label: "Torsdag" },
    { value: "fredag", label: "Fredag" },
    { value: "lördag", label: "Lördag" },
    { value: "söndag", label: "Söndag" },
  ];

  // Översättningstabell för veckodagar
  const veckodagsÖversättning = {
    monday: "måndag",
    tuesday: "tisdag",
    wednesday: "onsdag",
    thursday: "torsdag",
    friday: "fredag",
    saturday: "lördag",
    sunday: "söndag",
    Måndag: "måndag",
    Tisdag: "tisdag",
    Onsdag: "onsdag",
    Torsdag: "torsdag",
    Fredag: "fredag",
    Lördag: "lördag",
    Söndag: "söndag",
  };

  // Hjälpfunktion för att formatera veckodagsnamn
  const formatWeekdayLabel = (dag) => {
    if (!dag) return "";
    const normalized =
      veckodagsÖversättning[dag] || String(dag).toLowerCase().trim();
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  // Hjälpfunktion för att normalisera veckodagar till svenska
  const normalizeDaysToSwedish = (days = []) => {
    return days.map(
      (day) => veckodagsÖversättning[day] || String(day).toLowerCase().trim()
    );
  };

  // Hjälpfunktion för att lägga till nollor
  const pad = (n) => String(n).padStart(2, "0");

  // Hjälpfunktion för att formatera datum
  const formatDate = (date) => {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )}`;
  };

  // Beräkna påskdagen för ett givet år
  const getEasterSunday = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;

    return new Date(year, month - 1, day);
  };

  // Hjälpfunktion för att lägga till dagar till ett datum
  const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  };

  // Beräkna midsommarafton
  const getMidsummerEve = (year) => {
    for (let day = 19; day <= 25; day++) {
      const date = new Date(year, 5, day);
      if (date.getDay() === 5) return date;
    }
    return null;
  };

  // Beräkna alla helgons dag
  const getAllSaintsDay = (year) => {
    for (let offset = 0; offset <= 6; offset++) {
      const date = new Date(year, 9, 31 + offset);
      if (date.getDay() === 6) return date;
    }
    return null;
  };

  // Generera alla svenska helgdagar för ett år
  const generateHolidaysForYear = (year) => {
    const easterSunday = getEasterSunday(year);
    const goodFriday = addDays(easterSunday, -2);
    const easterMonday = addDays(easterSunday, 1);
    const ascensionDay = addDays(easterSunday, 39);
    const pentecost = addDays(easterSunday, 49);
    const midsummerEve = getMidsummerEve(year);
    const midsummerDay = midsummerEve ? addDays(midsummerEve, 1) : null;
    const allSaintsDay = getAllSaintsDay(year);

    const holidayMap = new Map();
    const defaultStartTime = "00:00";
    const defaultEndTime = "23:59";

    const holidays = [
      { name: "Nyårsdagen", date: new Date(year, 0, 1), amount: 0, startTime: defaultStartTime, endTime: defaultEndTime },
      { name: "Trettondedag jul", date: new Date(year, 0, 6), amount: 0, startTime: defaultStartTime, endTime: defaultEndTime },
      { name: "Långfredagen", date: goodFriday, amount: 0, startTime: defaultStartTime, endTime: defaultEndTime },
      { name: "Påskafton", date: addDays(easterSunday, -1), amount: 0, startTime: defaultStartTime, endTime: defaultEndTime },
      { name: "Påskdagen", date: easterSunday, amount: 0, startTime: defaultStartTime, endTime: defaultEndTime },
      { name: "Annandag påsk", date: easterMonday, amount: 0, startTime: defaultStartTime, endTime: defaultEndTime },
      { name: "Första maj", date: new Date(year, 4, 1), amount: 0, startTime: defaultStartTime, endTime: defaultEndTime },
      { name: "Kristi himmelsfärdsdag", date: ascensionDay, amount: 0, startTime: defaultStartTime, endTime: defaultEndTime },
      { name: "Pingstdagen", date: pentecost, amount: 0, startTime: defaultStartTime, endTime: defaultEndTime },
      { name: "Nationaldagen", date: new Date(year, 5, 6), amount: 0, startTime: defaultStartTime, endTime: defaultEndTime },
      ...(midsummerEve ? [{ name: "Midsommarafton", date: midsummerEve, amount: 0, startTime: defaultStartTime, endTime: defaultEndTime }] : []),
      ...(midsummerDay ? [{ name: "Midsommardagen", date: midsummerDay, amount: 0, startTime: defaultStartTime, endTime: defaultEndTime }] : []),
      ...(allSaintsDay ? [{ name: "Alla helgons dag", date: allSaintsDay, amount: 0, startTime: defaultStartTime, endTime: defaultEndTime }] : []),
      { name: "Julafton", date: new Date(year, 11, 24), amount: 0, startTime: defaultStartTime, endTime: defaultEndTime },
      { name: "Juldagen", date: new Date(year, 11, 25), amount: 0, startTime: defaultStartTime, endTime: defaultEndTime },
      { name: "Annandag jul", date: new Date(year, 11, 26), amount: 0, startTime: defaultStartTime, endTime: defaultEndTime },
      { name: "Nyårsafton", date: new Date(year, 11, 31), amount: 0, startTime: defaultStartTime, endTime: defaultEndTime },
    ];

    holidays.forEach((holiday) => {
      const dateStr = formatDate(holiday.date);
      if (!holidayMap.has(dateStr)) {
        holidayMap.set(dateStr, holiday);
      }
    });

    return Array.from(holidayMap.values())
      .map((holiday) => ({
        ...holiday,
        id: `${holiday.name}-${formatDate(holiday.date)}`,
        formatted: formatDate(holiday.date),
      }))
      .sort((a, b) => a.date - b.date);
  };

  // Hämta alla svenska helgdagar och spara i Firestore
  const fetchAllSwedishHolidays = async (year = selectedYear) => {
    if (!accountToUse || fetchingHolidays || loading || !Number.isInteger(year) || year < 2000 || year > 2100) return;

    setFetchingHolidays(true);

    try {
      const holidays = generateHolidaysForYear(year);
      const formattedHolidays = holidays.map((holiday) => ({
        name: holiday.name,
        date: holiday.formatted,
        year,
        amount: Number(holiday.amount || 0),
        startTime: holiday.startTime || "00:00",
        endTime: holiday.endTime || "23:59",
      }));

      await saveAllHolidaysToFirestore(formattedHolidays, year);
      setLastFetchedYear(year);
      setFetchedYears(prev => new Set([...prev, year]));

    } catch (error) {
      console.error("Fel vid hämtning av helgdagar:", error);
      alert("Ett fel uppstod vid hämtning av helgdagar. Försök igen.");
    } finally {
      setFetchingHolidays(false);
    }
  };

  // Spara alla helgdagar i Firestore
  const saveAllHolidaysToFirestore = async (holidays, year) => {
    if (!accountToUse) return;

    setLoading(true);

    try {
      const holidaysRef = collection(db, "users", accountToUse, "holidays");
      const q = query(holidaysRef, where("year", "==", year));
      const snapshot = await getDocs(q);

      const existingHolidays = new Set();
      snapshot.forEach((docItem) => {
        const holiday = docItem.data();
        existingHolidays.add(`${holiday.name}-${holiday.date}-${Number(holiday.year)}`);
      });

      const uniqueHolidaysMap = new Map();
      holidays.forEach((holiday) => {
        const key = `${holiday.name}-${holiday.date}-${holiday.year}`;
        if (!uniqueHolidaysMap.has(key)) {
          uniqueHolidaysMap.set(key, holiday);
        }
      });

      for (const holiday of uniqueHolidaysMap.values()) {
        if (existingHolidays.has(`${holiday.name}-${holiday.date}-${holiday.year}`)) continue;
        await addDoc(holidaysRef, {
          ...holiday,
          createdAt: new Date().toISOString(),
          createdBy: accountToUse,
          updatedAt: new Date().toISOString(),
        });
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error("Fel vid sparande av helgdagar:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Hämta OB-regler från Firestore
  useEffect(() => {
    if (!accountToUse || !isOpen) return;

    const obRulesRef = collection(db, "users", accountToUse, "obRules");
    const q = query(obRulesRef, orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const rules = [];
        snapshot.forEach((docItem) => {
          const data = docItem.data();
          rules.push({
            id: docItem.id,
            ...data,
            days: normalizeDaysToSwedish(data.days || []),
          });
        });
        setSavedRules(rules);
      },
      (error) => {
        console.error("Fel vid hämtning av OB-regler:", error);
      }
    );

    return () => unsubscribe();
  }, [accountToUse, isOpen]);

  // Hämta helgdagar från Firestore och uppdatera fetchedYears
  useEffect(() => {
    if (!accountToUse || !isOpen) return;

    const holidaysRef = collection(db, "users", accountToUse, "holidays");
    const q = query(holidaysRef, orderBy("date", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const holidays = [];
        const yearsWithData = new Set();

        snapshot.forEach((docItem) => {
          const data = docItem.data();
          holidays.push({
            id: docItem.id,
            ...data,
          });
          if (data.year) {
            yearsWithData.add(data.year);
          }
        });

        const uniqueHolidays = removeDuplicates(holidays);
        setSavedHolidays(uniqueHolidays);
        setFetchedYears(yearsWithData);
      },
      (error) => {
        console.error("Fel vid hämtning av helgdagar:", error);
      }
    );

    return () => unsubscribe();
  }, [accountToUse, isOpen]);

  // Ta bort dubbletter av helgdagar
  const removeDuplicates = (holidays) => {
    const seen = new Map();
    return holidays.filter((holiday) => {
      const key = `${holiday.date}-${holiday.year}`;
      if (seen.has(key)) return false;
      seen.set(key, true);
      return true;
    });
  };

  // Återställ formulär för OB-regler
  const resetForm = () => {
    setName("");
    setAmount("");
    setStartTime("");
    setEndTime("");
    setSelectedDays([]);
    setEditingRuleId(null);
    setSaveSuccess(false);
  };

  // Återställ formulär för helgdagar
  const resetHolidayForm = () => {
    setHolidayName("");
    setHolidayDate("");
    setHolidayYear(currentYear);
    setHolidayAmount("");
    setHolidayStartTime("");
    setHolidayEndTime("");
    setEditingHolidayId(null);
  };

  // Återställ vid öppning av popup
  useEffect(() => {
    if (isOpen) {
      resetForm();
      resetHolidayForm();
      setShowCreatePanel(false);
      setActiveTab("obRules");
      setSelectedYear(currentYear);
    }
  }, [isOpen, currentYear]);

  // Stäng popup
  const handleClose = () => {
    resetForm();
    resetHolidayForm();
    setShowCreatePanel(false);
    onClose?.();
  };

  // Återgå till listan


  // Hantera val av veckodag
  const handleDayChange = (dayValue) => {
    setSelectedDays((prev) =>
      prev.includes(dayValue)
        ? prev.filter((d) => d !== dayValue)
        : [...prev, dayValue]
    );
  };

  // Ta bort OB-regel
  const handleDeleteRule = async (id) => {
    if (!accountToUse) return;

    if (window.confirm("Är du säker på att du vill ta bort denna OB-regel?")) {
      try {
        await deleteDoc(doc(db, "users", accountToUse, "obRules", id));
      } catch (error) {
        console.error("Fel vid borttagning av OB-regel:", error);
      }
    }
  };

  // Ta bort helgdag
  const handleDeleteHoliday = async (id) => {
    if (!accountToUse) return;

    if (window.confirm("Är du säker på att du vill ta bort denna helgdag?")) {
      try {
        await deleteDoc(doc(db, "users", accountToUse, "holidays", id));
      } catch (error) {
        console.error("Fel vid borttagning av helgdag:", error);
      }
    }
  };

  // Redigera OB-regel
  // Spara OB-regel
  const handleSubmitRule = async (e) => {
    e.preventDefault();
    if (!accountToUse) return;

    setLoading(true);
    setSaveSuccess(false);

    try {
      const ruleData = {
        name,
        amount: Number(amount),
        startTime,
        endTime,
        days: normalizeDaysToSwedish(selectedDays),
        updatedAt: new Date().toISOString(),
      };

      if (editingRuleId) {
        const ruleRef = doc(db, "users", accountToUse, "obRules", editingRuleId);
        await updateDoc(ruleRef, ruleData);
      } else {
        const obRulesRef = collection(db, "users", accountToUse, "obRules");
        await addDoc(obRulesRef, {
          ...ruleData,
          createdAt: new Date().toISOString(),
          createdBy: accountToUse,
        });
      }

      setSaveSuccess(true);
      setTimeout(() => {
        resetForm();
        setShowCreatePanel(false);
        setSaveSuccess(false);
      }, 1000);
    } catch (error) {
      console.error("Fel vid sparande av OB-regel:", error);
    } finally {
      setLoading(false);
    }
  };

  // Spara helgdag
  const handleSubmitHoliday = async (e) => {
    e.preventDefault();
    if (!accountToUse) return;

    setLoading(true);
    setSaveSuccess(false);

    try {
      const holidayData = {
        name: holidayName,
        date: holidayDate,
        year: holidayYear,
        amount: Number(holidayAmount || 0),
        startTime: holidayStartTime || "00:00",
        endTime: holidayEndTime || "23:59",
        updatedAt: new Date().toISOString(),
      };

      if (editingHolidayId) {
        const holidayRef = doc(db, "users", accountToUse, "holidays", editingHolidayId);
        await updateDoc(holidayRef, holidayData);
      } else {
        const holidaysRef = collection(db, "users", accountToUse, "holidays");
        await addDoc(holidaysRef, {
          ...holidayData,
          createdAt: new Date().toISOString(),
          createdBy: accountToUse,
        });
      }

      setSaveSuccess(true);
      setTimeout(() => {
        resetHolidayForm();
        setShowCreatePanel(false);
        setSaveSuccess(false);
      }, 1000);
    } catch (error) {
      console.error("Fel vid sparande av helgdag:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const styles = {
    overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0, 0, 0, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "16px" },
    popup: { backgroundColor: "#ffffff", borderRadius: "22px", width: "88%", maxWidth: "1200px", height: "82vh", maxHeight: "820px", overflow: "hidden", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)", position: "relative", display: "flex", flexDirection: "column" },
    header: { padding: "20px 24px", borderBottom: "1px solid #eef2f7", display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#ffffff", flexShrink: 0 },
    headerLeft: { display: "flex", alignItems: "center", gap: "24px" },
    title: { margin: 0, fontSize: "1.8rem", fontWeight: 800, color: "#2563eb" },
    tabs: { display: "flex", gap: "8px", backgroundColor: "#f1f5f9", padding: "4px", borderRadius: "12px" },
    tabButton: (active) => ({ padding: "10px 20px", borderRadius: "10px", border: "none", backgroundColor: active ? "#ffffff" : "transparent", color: active ? "#2563eb" : "#64748b", fontSize: "0.95rem", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", boxShadow: active ? "0 2px 8px rgba(0,0,0,0.05)" : "none" }),
    closeButton: { background: "none", border: "none", cursor: "pointer", padding: "8px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280", flexShrink: 0 },
    container: { display: "flex", height: "100%", width: "200%", transition: "transform 0.35s ease-in-out", transform: showCreatePanel ? "translateX(-50%)" : "translateX(0)", overflow: "hidden", flex: 1 },
    mainContent: { flex: "0 0 50%", height: "100%", overflow: "hidden", backgroundColor: "#ffffff", display: "flex", flexDirection: "column" },
    createPanel: { flex: "0 0 50%", height: "100%", overflow: "hidden", backgroundColor: "#ffffff", borderLeft: "1px solid #eef2f7", display: "flex", flexDirection: "column" },
    contentWrapper: { padding: "24px 32px", height: "100%", overflowY: "auto", boxSizing: "border-box" },
    introText: { fontSize: "0.98rem", lineHeight: 1.6, color: "#64748b", marginBottom: "24px" },
    divider: { height: "1px", backgroundColor: "#eef2f7", margin: "24px 0" },
    sectionTitle: { margin: 0, marginBottom: "14px", fontSize: "1.35rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.25 },
    actionBar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", flexWrap: "wrap", gap: "16px" },
    addButton: { padding: "12px 18px", borderRadius: "12px", border: "none", backgroundColor: "#2563eb", color: "#ffffff", fontSize: "0.95rem", fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px", boxShadow: "0 8px 18px rgba(37,99,235,0.20)" },
    savedWrapper: { marginTop: "16px" },
    savedCard: { border: "1px solid #e5e7eb", borderRadius: "14px", padding: "16px", marginBottom: "12px", backgroundColor: "#f8fafc" },
    savedCardHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" },
    savedCardLeft: { flex: "1 1 320px", minWidth: 0 },
    savedCardRight: { display: "flex", alignItems: "flex-start", justifyContent: "flex-end", marginLeft: "auto" },
    savedCardTitle: { margin: 0, fontSize: "1rem", fontWeight: 700, color: "#0f172a" },
    savedCardAmount: { marginTop: "6px", fontSize: "0.95rem", fontWeight: 700, color: "#2563eb" },
    savedCardInfo: { fontSize: "0.92rem", lineHeight: 1.6, color: "#475569", marginTop: "10px" },
    savedCardActions: { display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end" },
    smallButton: { padding: "8px 12px", borderRadius: "8px", border: "1px solid #d1d5db", backgroundColor: "#ffffff", color: "#374151", fontSize: "13px", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap" },
    deleteSmallButton: { padding: "8px 12px", borderRadius: "8px", border: "none", backgroundColor: "#ef4444", color: "#ffffff", fontSize: "13px", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap" },
    emptyText: { fontSize: "0.95rem", color: "#6b7280", padding: "14px 16px", backgroundColor: "#f8fafc", borderRadius: "12px", border: "1px dashed #cbd5e1" },
    panelHeader: { padding: "24px 24px 16px 24px", borderBottom: "1px solid #eef2f7", display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#ffffff", position: "sticky", top: 0, zIndex: 10, flexShrink: 0 },
    panelHeaderLeft: { display: "flex", alignItems: "center", gap: "12px" },
    backButton: { background: "none", border: "none", cursor: "pointer", padding: "8px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "#007bff" },
    panelTitle: { margin: 0, fontSize: "1.5rem", fontWeight: 700, color: "#1f2937", display: "flex", alignItems: "center", gap: "10px" },
    form: { padding: "24px", height: "100%", overflowY: "auto", boxSizing: "border-box" },
    row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" },
    formGroup: { marginBottom: "20px" },
    label: { display: "block", fontSize: "14px", fontWeight: 600, color: "#374151", marginBottom: "8px" },
    input: { width: "100%", padding: "12px 16px", borderRadius: "12px", border: "1px solid #d1d5db", fontSize: "14px", outline: "none", boxSizing: "border-box", backgroundColor: "#ffffff" },
    timeWrap: { position: "relative" },
    timeInput: { width: "100%", padding: "12px 40px 12px 16px", borderRadius: "12px", border: "1px solid #d1d5db", fontSize: "14px", outline: "none", boxSizing: "border-box", backgroundColor: "#ffffff" },
    clockIcon: { position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", color: "#6b7280", pointerEvents: "none" },
    selectBox: { width: "100%", padding: "12px 16px", borderRadius: "12px", border: "1px solid #d1d5db", backgroundColor: "#ffffff", boxSizing: "border-box" },
    placeholder: { fontSize: "14px", color: "#9ca3af", marginBottom: "10px" },
    dayGrid: { display: "flex", flexWrap: "wrap", gap: "8px" },
    dayButton: (active) => ({ border: active ? "1px solid #007bff" : "1px solid #d1d5db", backgroundColor: active ? "#eff6ff" : "#ffffff", color: active ? "#007bff" : "#374151", borderRadius: "10px", padding: "8px 12px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }),
    buttonGroup: { display: "flex", gap: "12px", marginTop: "24px", marginBottom: "16px", flexWrap: "wrap" },
    cancelButton: { flex: 1, padding: "14px 20px", borderRadius: "12px", border: "1px solid #d1d5db", backgroundColor: "#ffffff", color: "#4b5563", fontSize: "15px", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", minWidth: "140px" },
    saveButton: { flex: 1, padding: "14px 20px", borderRadius: "12px", border: "none", backgroundColor: "#007bff", color: "#ffffff", fontSize: "15px", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", minWidth: "140px", opacity: loading ? 0.7 : 1 },
    successMessage: { backgroundColor: "#10b981", color: "#ffffff", padding: "10px", borderRadius: "8px", textAlign: "center", marginTop: "16px", fontSize: "14px" },
    yearSelector: { display: "flex", gap: "12px", alignItems: "center", marginBottom: "20px", flexWrap: "wrap" },
    yearInput: { width: "110px", padding: "10px 12px", borderRadius: "10px", border: "1px solid #d1d5db", fontSize: "14px", outline: "none" },
    fetchButton: (isDisabled) => ({
      padding: "10px 16px",
      borderRadius: "10px",
      border: "none",
      backgroundColor: isDisabled ? "#9ca3af" : "#16a34a",
      color: "#ffffff",
      fontSize: "14px",
      fontWeight: 700,
      cursor: isDisabled ? "not-allowed" : "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      boxShadow: isDisabled ? "none" : "0 8px 18px rgba(22,163,74,0.18)",
      opacity: isDisabled ? 0.6 : 1
    }),
    lastFetched: { fontSize: "13px", color: "#6b7280", marginTop: "8px", fontStyle: "italic" },
  };

  const isYearFetched = fetchedYears.has(selectedYear);
  const isFetchButtonDisabled = fetchingHolidays || loading || !Number.isInteger(selectedYear) || selectedYear < 2000 || selectedYear > 2100;

  Object.assign(styles, {
    overlay: { width: "100%", minWidth: 0, background: "#f7f7f8" },
    popup: { width: "100%", minWidth: 0 },
    header: { padding: "20px 12px 16px" },
    headerLeft: { display: "flex", alignItems: "flex-start", flexDirection: "column", gap: 20 },
    title: { margin: 0, fontSize: 24, fontWeight: 800, color: "#111827", display: "flex", alignItems: "center", gap: 12 },
    container: { display: "flex", flexDirection: "column", gap: 24, padding: "0 12px 16px" },
    mainContent: { order: 2, minWidth: 0 },
    createPanel: { order: 1, background: "white", border: "1px solid #d1d5db", borderRadius: 8 },
    contentWrapper: { padding: 0 },
    panelHeader: { padding: "20px 20px 0", display: "flex" },
    panelTitle: { ...styles.panelTitle, fontSize: 16 },
    form: { padding: 20 },
    buttonGroup: { display: "flex", justifyContent: "flex-end", marginTop: 20 },
    saveButton: { ...styles.saveButton, flex: "initial", backgroundColor: "#1677ff", borderRadius: 8 },
  });
  return (
    <div className="payroll-rules-page" style={styles.overlay}>
      <div style={styles.popup} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <button type="button" onClick={handleClose} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", border: "1px solid #3b82f6", borderRadius: 8, color: "#2563eb", background: "white", cursor: "pointer", fontWeight: 700 }}><FaArrowLeft />Tillbaka till löner</button>
            <h1 style={styles.title}><FaCloudMoon color="#1677ff" />OB-regler &amp; Helgdagar</h1>
            <div style={styles.tabs}>
              <button style={styles.tabButton(activeTab === "obRules")} onClick={() => setActiveTab("obRules")}>
                <FaList size={14} /> OB-regler
              </button>
              <button style={styles.tabButton(activeTab === "holidays")} onClick={() => setActiveTab("holidays")}>
                <FaCalendarAlt size={14} /> Helgdagar
              </button>
            </div>
          </div>

        </div>

        <div style={styles.container}>
          {/* Skapa/redigera panel */}
          <div style={styles.createPanel}>
            <div style={styles.panelHeader}>
              <div style={styles.panelHeaderLeft}>

                <h2 style={styles.panelTitle}>
                  {activeTab === "obRules" ? (
                    <><FaCloudMoon color="#007bff" /> {editingRuleId ? "Redigera OB-regel" : "Lägg till ny OB-regel"}</>
                  ) : (
                    <><FaCalendarAlt color="#007bff" /> {editingHolidayId ? "Redigera helgdag" : "Lägg till ny helgdag"}</>
                  )}
                </h2>
              </div>

            </div>

            {activeTab === "obRules" ? (
              <form style={styles.form} onSubmit={handleSubmitRule}>
                <div style={styles.row2}>
                  <div style={styles.formGroup}>
                    <label className="payroll-label" style={styles.label}>Namn på OB-regel</label>
                    <input type="text" className="payroll-field" style={styles.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex. OB-A, Helg-OB, Natt-OB..." required />
                  </div>
                  <div style={styles.formGroup}>
                    <label className="payroll-label" style={styles.label}>Mängd pengar tillägg i kronor</label>
                    <input type="number" className="payroll-field" style={styles.input} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Ange belopp per timme" required min="0" step="0.01" />
                  </div>
                </div>
                <div style={styles.row2}>
                  <div style={styles.formGroup}>
                    <label className="payroll-label" style={styles.label}>Starttid</label>
                    <div style={styles.timeWrap}>
                      <input type="time" style={styles.timeInput} value={startTime} onChange={(e) => setStartTime(e.target.value)} required />
                      <FaClock style={styles.clockIcon} size={14} />
                    </div>
                  </div>
                  <div style={styles.formGroup}>
                    <label className="payroll-label" style={styles.label}>Sluttid</label>
                    <div style={styles.timeWrap}>
                      <input type="time" style={styles.timeInput} value={endTime} onChange={(e) => setEndTime(e.target.value)} required />
                      <FaClock style={styles.clockIcon} size={14} />
                    </div>
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label className="payroll-label" style={styles.label}>Välj veckodagar</label>
                  <div style={styles.selectBox}>
                    {selectedDays.length === 0 && <div style={styles.placeholder}>Klicka på dagarna som ska gälla...</div>}
                    <div style={styles.dayGrid}>
                      {veckodagsalternativ.map((day) => (
                        <button key={day.value} type="button" style={styles.dayButton(selectedDays.includes(day.value))} onClick={() => handleDayChange(day.value)}>
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={styles.divider} />
                <div style={styles.buttonGroup}>

                  <button type="submit" className="payroll-primary-button" style={styles.saveButton} disabled={loading}><FaSave /> {loading ? "Sparar..." : editingRuleId ? "Uppdatera" : "Spara"}</button>
                </div>
                {saveSuccess && <div className="payroll-success" style={styles.successMessage}>OB-regel {editingRuleId ? "uppdaterades" : "sparades"} framgångsrikt!</div>}
              </form>
            ) : (
              <form style={styles.form} onSubmit={handleSubmitHoliday}>
                <div style={styles.formGroup}>
                  <label className="payroll-label" style={styles.label}>Namn på helgdag</label>
                  <input type="text" className="payroll-field" style={styles.input} value={holidayName} onChange={(e) => setHolidayName(e.target.value)} placeholder="Ex. Julafton, Midsommarafton..." required />
                </div>
                <div style={styles.row2}>
                  <div style={styles.formGroup}>
                    <label className="payroll-label" style={styles.label}>Datum (ÅÅÅÅ-MM-DD)</label>
                    <input type="date" className="payroll-field" style={styles.input} value={holidayDate} onChange={(e) => setHolidayDate(e.target.value)} required />
                  </div>
                  <div style={styles.formGroup}>
                    <label className="payroll-label" style={styles.label}>År</label>
                    <input type="number" className="payroll-field" style={styles.input} value={holidayYear} onChange={(e) => setHolidayYear(Number(e.target.value))} min="2000" max="2100" required />
                  </div>
                </div>
                <div style={styles.row2}>
                  <div style={styles.formGroup}>
                    <label className="payroll-label" style={styles.label}>Starttid</label>
                    <div style={styles.timeWrap}>
                      <input type="time" style={styles.timeInput} value={holidayStartTime} onChange={(e) => setHolidayStartTime(e.target.value)} required />
                      <FaClock style={styles.clockIcon} size={14} />
                    </div>
                  </div>
                  <div style={styles.formGroup}>
                    <label className="payroll-label" style={styles.label}>Sluttid</label>
                    <div style={styles.timeWrap}>
                      <input type="time" style={styles.timeInput} value={holidayEndTime} onChange={(e) => setHolidayEndTime(e.target.value)} required />
                      <FaClock style={styles.clockIcon} size={14} />
                    </div>
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label className="payroll-label" style={styles.label}>Mängd pengar tillägg i kronor</label>
                  <input type="number" className="payroll-field" style={styles.input} value={holidayAmount} onChange={(e) => setHolidayAmount(e.target.value)} placeholder="Ange belopp per timme" required min="0" step="0.01" />
                </div>
                <div style={styles.divider} />
                <div style={styles.buttonGroup}>

                  <button type="submit" className="payroll-primary-button" style={styles.saveButton} disabled={loading}><FaSave /> {loading ? "Sparar..." : editingHolidayId ? "Uppdatera" : "Spara"}</button>
                </div>
                {saveSuccess && <div className="payroll-success" style={styles.successMessage}>Helgdag {editingHolidayId ? "uppdaterades" : "sparades"} framgångsrikt!</div>}
              </form>
            )}
          </div>
          <div style={styles.mainContent}>
            <div style={styles.contentWrapper}>
              {activeTab === "obRules" ? (
                <>
                  <div style={styles.actionBar}>
                    <h2 style={styles.sectionTitle}>Alla sparade OB-regler</h2>
                  </div>
                  <div className="payroll-table-wrap"><table className="payroll-table"><thead><tr><th>Namn</th><th>Belopp</th><th>Tid</th><th>Dagar</th><th>Åtgärder</th></tr></thead><tbody>
                    {savedRules.map(rule => <EditablePayrollRow key={rule.id} item={rule} companyId={accountToUse} collectionName="obRules" onDelete={item => handleDeleteRule(item.id)} columns={[{ value: rule.name, fields: [{ key: "name", label: "OB-namn i raden", required: true }] },
{ value: `${Number(rule.amount || 0).toLocaleString("sv-SE")} kr/timme`, fields: [{ key: "amount", label: "OB-belopp i raden", type: "number", min: 0, required: true }] },
{ value: `${rule.startTime} – ${rule.endTime}`, fields: [{ key: "startTime", label: "Starttid i raden", type: "time", required: true }, { key: "endTime", label: "Sluttid i raden", type: "time", required: true }] },
{ value: rule.days?.map(formatWeekdayLabel).join(", ") || "–", fields: [{ key: "days", label: "Dagar", type: "days" }] },]} />)}
                    {!savedRules.length && <tr><td colSpan={5} className="payroll-empty">Inga OB-regler ännu.</td></tr>}
                  </tbody></table></div>
                </>
              ) : (
                <>
                  <div style={styles.actionBar}>
                    <h2 style={styles.sectionTitle}>Alla sparade helgdagar</h2>
                  </div>
                  <div className="holiday-year-toolbar">
                    <div className="holiday-year-field">
                    <label htmlFor="holiday-list-year" className="payroll-label">Välj år</label>
                    <input
                      id="holiday-list-year"
                      className="payroll-field"
                      type="number"
                      value={selectedYear}
                      onChange={(e) => {
                        const newYear = Number(e.target.value);
                        setSelectedYear(newYear);
                        if (lastFetchedYear !== newYear) {
                          setLastFetchedYear(null);
                        }
                      }}
                      min="2000"
                      max="2100"
                    />
                    </div>
                    <button
                      type="button"
                      className="holiday-fetch-button payroll-primary-button"
                      onClick={() => fetchAllSwedishHolidays(selectedYear)}
                      disabled={isFetchButtonDisabled}
                    >
                      <FaDownload size={13} />
                      {fetchingHolidays
                        ? "Hämtar helgdagar..."
                        : isYearFetched
                          ? "Hämta helgdagar igen"
                          : "Hämta aktuella helgdagar för året"
                      }
                    </button>
                  </div>
                  {lastFetchedYear === selectedYear && !isYearFetched && (
                    <div className="holiday-year-status" role="status">✓ Svenska helgdagar hämtades för {selectedYear}</div>
                  )}
                  {isYearFetched && (
                    <div className="holiday-year-status" role="status">✓ Helgdagar för {selectedYear} finns redan i systemet</div>
                  )}
                  <div className="payroll-table-wrap"><table className="payroll-table"><thead><tr><th>Namn</th><th>Datum</th><th>Tid</th><th>Belopp</th><th>Åtgärder</th></tr></thead><tbody>
                    {savedHolidays.filter(holiday => holiday.year === selectedYear).map(holiday => <EditablePayrollRow key={holiday.id} item={holiday} companyId={accountToUse} collectionName="holidays" onDelete={item => handleDeleteHoliday(item.id)} columns={[{ value: holiday.name, fields: [{ key: "name", label: "Helgdagsnamn i raden", required: true }] },
{ value: holiday.date, fields: [{ key: "date", label: "Datum i raden", type: "date", required: true }] },
{ value: `${holiday.startTime || "00:00"} – ${holiday.endTime || "23:59"}`, fields: [{ key: "startTime", label: "Starttid i raden", type: "time" }, { key: "endTime", label: "Sluttid i raden", type: "time" }] },
{ value: `${Number(holiday.amount || 0).toLocaleString("sv-SE")} kr/timme`, fields: [{ key: "amount", label: "Helgdagsbelopp i raden", type: "number", min: 0, required: true }] },]} />)}
                    {!savedHolidays.some(holiday => holiday.year === selectedYear) && <tr><td colSpan={5} className="payroll-empty">Inga helgdagar för {selectedYear}.</td></tr>}
                  </tbody></table></div>
                  {saveSuccess && <div className="payroll-success" style={styles.successMessage}>Helgdagar hämtades och sparades framgångsrikt!</div>}
                </>
              )}
            </div>
          </div>


        </div>
      </div>
    </div>
  );
}
